import { z } from "zod";

const CONTAINER_KEY_MAP = {
	id: "id",
	dimension1: "dimension1",
	dimension2: "dimension2",
	dimension3: "dimension3",
	volume: "volume",
	ratio: "ratio",
	surfacearea: "surfaceArea",
	distance: "distance",
	maximumweight: "maximumWeight",
} as const;

const normalizeContainerKeys = (value: unknown): unknown => {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return value;
	}

	const normalizedValue: Record<string, unknown> = {};

	for (const [key, keyValue] of Object.entries(value)) {
		const normalizedKey = CONTAINER_KEY_MAP[key.toLowerCase() as keyof typeof CONTAINER_KEY_MAP];
		normalizedValue[normalizedKey ?? key] = keyValue;
	}

	return normalizedValue;
};

const extractUploadedContainers = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value;
	}

	if (typeof value !== "object" || value === null) {
		return value;
	}

	const entries = Object.entries(value);
	const boxListEntry = entries.find(([key]) => key.toLowerCase() === "boxlist");

	return boxListEntry?.[1] ?? value;
};

const orderByOptionSchema = z.enum(["percent-filled", "distance", "surface-area", "volume", ""]);

export const containerFormSchema = z.object({
	id: z.number().optional(),
	dimension1: z.number().optional(),
	dimension2: z.number().optional(),
	dimension3: z.number().optional(),
	volume: z.number().optional(),
	ratio: z.number().optional(),
	surfaceArea: z.number().optional(),
	distance: z.number().optional(),
	maximumWeight: z.number().optional(),
});

const containerSchema = z.preprocess(normalizeContainerKeys, containerFormSchema);

export type Container = z.infer<typeof containerSchema>;

export const containersSchema = z.array(containerSchema);
export const containersFormSchema = z.array(containerFormSchema);
export const uploadedContainersSchema = z.preprocess(extractUploadedContainers, containersSchema);

const selectionOptionsSchema = z.object({
	orderBy: orderByOptionSchema.nullable().optional(),
	cutoffTime: z.coerce.number().min(0).default(0),
	shrinkwrap: z.boolean().default(true),
	maxCartons: z.coerce.number().min(0).default(0),
	shrinkwrapMinDim1: z.number().optional(),
	shrinkwrapMinDim2: z.number().optional(),
	shrinkwrapMinDim3: z.number().optional(),
	shrinkwrapMaxDim1: z.number().optional(),
	shrinkwrapMaxDim2: z.number().optional(),
	shrinkwrapMaxDim3: z.number().optional(),
});

const containerOptionsSchema = z.object({
	minimumDimension1: z.coerce.number().min(0).default(0),
	maximumDimension1: z.coerce.number().min(0).default(0),
	incrementDimension1By: z.coerce.number().min(0).default(2),
	minimumDimension2: z.coerce.number().min(0).default(0),
	maximumDimension2: z.coerce.number().min(0).default(0),
	incrementDimension2By: z.coerce.number().min(0).default(2),
	minimumDimension3: z.coerce.number().min(0).default(0),
	maximumDimension3: z.coerce.number().min(0).default(0),
	incrementDimension3By: z.coerce.number().min(0).default(2),
	maximumWeight: z.coerce.number().min(0).default(0),
});

export const cubeConfigFormSchema = z.object({
	configurations: z
		.record(
			z.string(),
			z.object({
				name: z.string(),
				boxList: containersSchema.nullable(),
				containerOptions: containerOptionsSchema.nullable(),
				isDefault: z.boolean().default(false),
				selectionOptions: selectionOptionsSchema.default({
					cutoffTime: 0,
					shrinkwrap: true,
					maxCartons: 0,
				}),
				limit: z.coerce.number().min(0).default(1),
				maximumWeight: z.coerce.number().min(0).default(1),
			}),
		)
		.nullable()
		.default(null),
	useBoxList: z.boolean().default(false),
	selectionOptions: selectionOptionsSchema.default({
		cutoffTime: 0,
		shrinkwrap: true,
		maxCartons: 0,
	}),
	containerOptions: containerOptionsSchema.default({
		minimumDimension1: 0,
		maximumDimension1: 0,
		incrementDimension1By: 2,
		minimumDimension2: 0,
		maximumDimension2: 0,
		incrementDimension2By: 2,
		minimumDimension3: 0,
		maximumDimension3: 0,
		incrementDimension3By: 2,
		maximumWeight: 0,
	}),
	limit: z.coerce.number().min(0).default(1),
	boxList: containersSchema.default([]),
	filename: z.string().nullable().default(null),
});

export const cubeConfigSchema = cubeConfigFormSchema.extend({
	id: z.string().optional(),
	tenantId: z.string().optional(),
	selectionOptions: selectionOptionsSchema.nullable().optional(),
	containerOptions: containerOptionsSchema.nullable().optional(),
	boxList: containersSchema.nullable().optional(),
	useBoxList: z.boolean().optional(),
});

export type CubeConfigFormValues = z.infer<typeof cubeConfigFormSchema>;
export type CubeConfig = z.infer<typeof cubeConfigSchema>;
